
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { featuredArticles, recentArticles, categories } from '../../mocks/articles';

export default function MediaHome() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [allArticles, setAllArticles] = useState(recentArticles);

  // ローカルストレージから投稿された記事を取得する関数
  const loadArticles = () => {
    const userArticles = JSON.parse(localStorage.getItem('userArticles') || '[]');
    // 特定の記事を除外（タイトルで判定）
    const filteredUserArticles = userArticles.filter(article => 
      article.title !== 'AIとHologramが描く未来のテクノロジー最前線'
    );
    const combinedArticles = [...filteredUserArticles, ...recentArticles];
    setAllArticles(combinedArticles);
    
    // ローカルストレージも更新
    localStorage.setItem('userArticles', JSON.stringify(filteredUserArticles));
  };

  // 初回読み込み時とフォーカス時に記事を取得
  useEffect(() => {
    loadArticles();
    
    // ページにフォーカスが戻った時に記事を再読み込み
    const handleFocus = () => {
      loadArticles();
    };

    // 記事更新イベントリスナー
    const handleArticleUpdate = () => {
      loadArticles();
    };
    
    window.addEventListener('focus', handleFocus);
    window.addEventListener('articleUpdated', handleArticleUpdate);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('articleUpdated', handleArticleUpdate);
    };
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredArticles.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredArticles.length) % featuredArticles.length);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gray-900 text-white py-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <button 
                onClick={() => navigate('/')}
                className="text-2xl font-bold hover:text-blue-400 transition-colors cursor-pointer"
              >
                ホログラム
              </button>
              <span className="text-blue-400 text-lg">MEDIA</span>
            </div>
            
            <nav className="hidden md:flex items-center space-x-6">
              <button 
                onClick={() => navigate('/media')}
                className="text-blue-400 cursor-pointer"
              >
                ホーム
              </button>
              <button 
                onClick={() => navigate('/media/articles')}
                className="hover:text-blue-400 transition-colors cursor-pointer"
              >
                記事一覧
              </button>
              <button 
                onClick={() => navigate('/media/post')}
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                記事を投稿
              </button>
              <button 
                onClick={() => navigate('/admin/login')}
                className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                管理者
              </button>
            </nav>
            
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="search"
                  placeholder="記事を検索..."
                  className="bg-gray-800 text-white px-4 py-2 rounded-full pl-10 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Featured Articles Carousel */}
      <section className="relative h-96 overflow-hidden">
        {featuredArticles.map((article, index) => (
          <div
            key={article.id}
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url('${article.image}')`
            }}
          >
            <div className="max-w-7xl mx-auto px-4 h-full flex items-center">
              <div className="text-white max-w-2xl">
                <span className="bg-blue-600 px-3 py-1 rounded-full text-sm font-medium mb-4 inline-block">
                  {article.category}
                </span>
                <h1 
                  onClick={() => navigate(`/media/articles/${article.id}`)}
                  className="text-4xl font-bold mb-4 leading-tight cursor-pointer hover:text-blue-300 transition-colors"
                >
                  {article.title}
                </h1>
                <p 
                  onClick={() => navigate(`/media/articles/${article.id}`)}
                  className="text-xl mb-6 text-gray-200 cursor-pointer hover:text-white transition-colors"
                >
                  {article.excerpt}
                </p>
                <div className="flex items-center space-x-4 mb-6">
                  <span className="text-sm">{article.date}</span>
                  <span className="text-sm">by {article.author}</span>
                </div>
                <button 
                  onClick={() => navigate(`/media/articles/${article.id}`)}
                  className="bg-blue-600 hover:bg-blue-7
                  text-white px-6 py-3 rounded-full font-semibold transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  記事を読む
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all z-20 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>

        <button
          onClick={nextSlide}
          className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all z-20 cursor-pointer"
        >
          <i className="ri-arrow-right-line text-xl"></i>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 z-20">
          {featuredArticles.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                index === currentSlide
                  ? 'bg-white scale-125'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Articles Grid */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-3xl font-bold text-gray-900">最新記事</h2>
              <button 
                onClick={() => navigate('/media/articles')}
                className="text-blue-600 hover:text-blue-700 font-semibold cursor-pointer"
              >
                すべて見る <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allArticles.slice(0, 6).map((article) => (
                <article key={article.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all transform hover:scale-105 cursor-pointer">
                  <div 
                    onClick={() => navigate(`/media/articles/${article.id}`)}
                    className="cursor-pointer"
                  >
                    <img 
                      src={article.mainImage || article.image}
                      alt={article.title}
                      className="w-full h-48 object-cover object-center"
                    />
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs font-medium">
                          {article.category}
                        </span>
                        <span className="text-gray-500 text-xs">{article.date}</span>
                      </div>
                      <h3 className="font-bold text-lg mb-2 leading-tight hover:text-blue-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>by {article.author}</span>
                        <div className="flex items-center">
                          <i className="ri-eye-line mr-1"></i>
                          <span>{article.views?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {/* Categories */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
              <h3 className="text-xl font-bold mb-6">カテゴリー</h3>
              <div className="space-y-3">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    className="flex items-center justify-between w-full text-left hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    <span>{category.name}</span>
                    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                      {category.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Popular Articles */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold mb-6">人気記事</h3>
              <div className="space-y-4">
                {allArticles.slice(0, 5).map((article) => (
                  <div 
                    key={article.id}
                    onClick={() => navigate(`/media/articles/${article.id}`)}
                    className="flex space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                  >
                    <img 
                      src={article.sidebarImage || article.image}
                      alt={article.title}
                      className="w-16 h-16 object-cover object-center rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2">
                        {article.title}
                      </h4>
                      <div className="flex items-center text-xs text-gray-500">
                        <span>{article.date}</span>
                        <span className="mx-2">•</span>
                        <div className="flex items-center">
                          <i className="ri-eye-line mr-1"></i>
                          <span>{article.views?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <section className="bg-blue-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            最新記事をメールでお届け
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            AI・ホログラム技術の最新情報を定期的にお送りします
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="メールアドレスを入力"
              className="flex-1 px-4 py-3 rounded-full focus:outline-none focus:ring-2 focus:ring-white"
            />
            <button className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-full font-semibold transition-all cursor-pointer whitespace-nowrap">
              購読する
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold mb-4">HOLOGRAM MEDIA</h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                AIとホログラム技術の最新情報をお届けするオウンドメディアです。
                業界の最新トレンドから技術解説まで幅広くカバーしています。
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors">
                  <i className="ri-twitter-line"></i>
                </div>
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors">
                  <i className="ri-facebook-line"></i>
                </div>
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors">
                  <i className="ri-linkedin-line"></i>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">コンテンツ</h4>
              <ul className="space-y-2 text-gray-300">
                <li><button onClick={() => navigate('/media/articles')} className="hover:text-white cursor-pointer">記事一覧</button></li>
                <li><button onClick={() => navigate('/media/post')} className="hover:text-white cursor-pointer">記事投稿</button></li>
                <li><a href="#" className="hover:text-white cursor-pointer">カテゴリー</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">人気記事</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">リンク</h4>
              <ul className="space-y-2 text-gray-300">
                <li><button onClick={() => navigate('/')} className="hover:text-white cursor-pointer">メインサイト</button></li>
                <li><a href="#" className="hover:text-white cursor-pointer">お問い合わせ</a></li>
                <li><a href="#" className="hover:text-white cursor-pointer">プライバシーポリシー</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 AI Code グループ MEDIA. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
